import { Candidate } from '../app/feathers/candidates/Candidate';
import { CandidateDraft } from '../app/feathers/candidates/CandidateDraft';

export const PROFILING_REASON_CODES = {
  CANDIDATE_PUBLISHED: 'CANDIDATE_PUBLISHED',
  PROFILE_REVIEWED: 'PROFILE_REVIEWED',
  PROFILER_RAN_RECENTLY: 'PROFILER_RAN_RECENTLY',
  RESEARCHER_LAST_CONTRIBUTED: 'RESEARCHER_LAST_CONTRIBUTED',
} as const;

export type ProfilingReasonCode = typeof PROFILING_REASON_CODES[keyof typeof PROFILING_REASON_CODES];

export interface ProfilingDates {
  profilerLastRunDate: Date | null;
  candidateLastPublishedDate: Date | null;
  lastReviewDate: Date | null;
  candidateProfilerVersion: string | undefined;
  researcherLastContributedDate: Date | null;
  shouldProfile: boolean;
  reason?: string;
  reasonCode?: ProfilingReasonCode;
}

/**
 * Calculate profiling decision dates from candidate data
 */
export function calculateProfilingDates(
  candidate: Candidate & { previousDrafts?: CandidateDraft[]; stagingDraft?: CandidateDraft }
): ProfilingDates {
  const previousDrafts = candidate.previousDrafts || [];
  const stagingDraft = candidate.stagingDraft;

  // 1. profilerLastRunDate: first draft with systemUpdateSource of 'profiler', get createdDate
  let profilerLastRunDate: Date | null = null;
  const allDrafts = stagingDraft ? [stagingDraft, ...previousDrafts] : previousDrafts;
  for (const draft of allDrafts) {
    if (draft.systemUpdateSource === 'profiler' && draft.createdAt) {
      profilerLastRunDate = new Date(draft.createdAt);
      break; // Get the first one (most recent since we're iterating from stagingDraft first)
    }
  }

  // 2. candidateLastPublishedDate: look through previousDrafts.workflowStatus, stagingDraft.workflowStatus, and current workflow.status
  let candidateLastPublishedDate: Date | null = null;
  // Check staging draft
  if (stagingDraft?.workflowStatus === 'published' && stagingDraft.createdAt) {
    const draftDate = new Date(stagingDraft.createdAt);
    candidateLastPublishedDate = draftDate;
  }
  // Check previous drafts
  for (const draft of previousDrafts) {
    if (draft.workflowStatus === 'published' && draft.createdAt) {
      const draftDate = new Date(draft.createdAt);
      if (!candidateLastPublishedDate || (draftDate && candidateLastPublishedDate && draftDate > candidateLastPublishedDate)) {
        candidateLastPublishedDate = draftDate;
      }
    }
  }
  // Check current workflow status
  if (candidate.workflow?.status === 'published' && candidate.updatedAt) {
    const currentDate = new Date(candidate.updatedAt);
    if (!candidateLastPublishedDate || (currentDate && candidateLastPublishedDate && currentDate > candidateLastPublishedDate)) {
      candidateLastPublishedDate = currentDate;
    }
  }

  // 3. lastReviewDate: same as above but for 'auto-profile-reviewed'
  let lastReviewDate: Date | null = null;
  // Check staging draft
  if (stagingDraft?.workflowStatus === 'auto-profile-reviewed' && stagingDraft.createdAt) {
    const draftDate = new Date(stagingDraft.createdAt);
    lastReviewDate = draftDate;
  }
  // Check previous drafts
  for (const draft of previousDrafts) {
    if (draft.workflowStatus === 'auto-profile-reviewed' && draft.createdAt) {
      const draftDate = new Date(draft.createdAt);
      if (!lastReviewDate || (draftDate && lastReviewDate && draftDate > lastReviewDate)) {
        lastReviewDate = draftDate;
      }
    }
  }
  // Check current workflow status
  if (candidate.workflow?.status === 'auto-profile-reviewed' && candidate.updatedAt) {
    const currentDate = new Date(candidate.updatedAt);
    if (!lastReviewDate || (currentDate && lastReviewDate && currentDate > lastReviewDate)) {
      lastReviewDate = currentDate;
    }
  }

  // 4. candidateProfilerVersion: from candidate.candidateProfilerVersion
  const candidateProfilerVersion = candidate.candidateProfilerVersion;

  // 5. researcherLastContributedDate: drafts after profilerLastRunDate where user is defined
  let researcherLastContributedDate: Date | null = null;
  if (profilerLastRunDate) {
    const draftsAfterProfilerRun = allDrafts.filter(draft => {
      if (!draft.createdAt) return false;
      const draftDate = new Date(draft.createdAt);
      return profilerLastRunDate && draftDate > profilerLastRunDate && Boolean(draft.user);
    });
    
    for (const draft of draftsAfterProfilerRun) {
      if (draft.createdAt) {
        const draftDate = new Date(draft.createdAt);
        if (!researcherLastContributedDate || (draftDate && researcherLastContributedDate && draftDate > researcherLastContributedDate)) {
          researcherLastContributedDate = draftDate;
        }
      }
    }
  } else {
    // If no profilerLastRunDate, check all drafts with user
    const draftsWithUser = allDrafts.filter(draft => draft.user != null);
    for (const draft of draftsWithUser) {
      if (draft.createdAt) {
        const draftDate = new Date(draft.createdAt);
        if (!researcherLastContributedDate || (draftDate && researcherLastContributedDate && draftDate > researcherLastContributedDate)) {
          researcherLastContributedDate = draftDate;
        }
      }
    }
  }

  // Determine if we should profile based on the calculated dates
  let shouldProfile = true;
  let reason: string | undefined;
  let reasonCode: ProfilingReasonCode | undefined;

  // 1. If candidateLastPublishedDate is defined, then do not profile
  if (candidateLastPublishedDate) {
    shouldProfile = false;
    reason = `Candidate was published on ${candidateLastPublishedDate.toISOString()}`;
    reasonCode = PROFILING_REASON_CODES.CANDIDATE_PUBLISHED;
  }
  // 2. If lastReviewDate is defined, do not profile
  else if (lastReviewDate) {
    shouldProfile = false;
    reason = `Profile was reviewed on ${lastReviewDate.toISOString()}`;
    reasonCode = PROFILING_REASON_CODES.PROFILE_REVIEWED;
  }
  // 3. If researcherLastContributedDate is defined, do not profile (unless workflow is 'references-added')
  else if (researcherLastContributedDate && candidate.workflow?.status !== 'references-added') {
    shouldProfile = false;
    reason = `Researcher last contributed on ${researcherLastContributedDate.toISOString()}`;
    reasonCode = PROFILING_REASON_CODES.RESEARCHER_LAST_CONTRIBUTED;
  }
  // 4. If profilerLastRunDate is undefined, then run
  else if (!profilerLastRunDate) {
    shouldProfile = true;
  }
  // 5. If profilerLastRunDate is within the last 15 minutes, then do not run
  else {
    const fifteenMinutesAgo = new Date(Date.now() - 15 * 60 * 1000);
    if (profilerLastRunDate && profilerLastRunDate > fifteenMinutesAgo) {
      shouldProfile = false;
      reason = `Profiler ran recently on ${profilerLastRunDate.toISOString()}`;
      reasonCode = PROFILING_REASON_CODES.PROFILER_RAN_RECENTLY;
    } else {
      // 6. Otherwise go ahead and run
      shouldProfile = true;
    }
  }

  return {
    profilerLastRunDate,
    candidateLastPublishedDate,
    lastReviewDate,
    candidateProfilerVersion,
    researcherLastContributedDate,
    shouldProfile,
    reason,
    reasonCode,
  };
}
 